/**
 * DTOs for the admin Clients (clients) endpoints. Mirrors the server
 * CamelDto in apps/api-py/src/admin/clients/dto.py.
 *
 * `clients` is the tenant root, so these rows are the clients
 * themselves rather than one client's data.
 */

/** Current enrollment periods grouped by their immutable tier snapshot. */
export interface EnrollmentCountByTier {
  essential: number
  pro: number
  premium: number
  unlimited: number
}

export const ENROLLMENT_TIERS: (keyof EnrollmentCountByTier)[] = [
  'essential',
  'pro',
  'premium',
  'unlimited',
]

/** One customer HR identity attached to this client. */
export interface HrUserRow {
  userId: string
  email: string
  name: string
  role: 'hr'
}

/** One client in the operator list. */
export interface ClientRow {
  clientId: string
  name: string
  /** Operator handle. Unique, and appears in no student-facing URL. */
  slug: string
  /**
   * Data classification, not authorization: excludes this client's
   * activity from customer analytics. It never widens access.
   */
  clientEmployeeIdEnabled?: boolean
  clientDepartmentEnabled?: boolean
  isInternal: boolean
  contractTrophyTarget: number
  /** Active students only — HR users and soft-deleted rows excluded. */
  studentCount: number
  /** Active HR users only — students and soft-deleted rows excluded. */
  hrUserCount: number
  /** Every live cohort, ended or not — the denominator to the next one. */
  cohortCount: number
  /** Live cohorts whose access end has not passed (ends inclusive). */
  joinableCohortCount: number
  /** Joinable cohorts with every required enrollment snapshot value. */
  enrollableCohortCount: number
  createdAt: string
}

export interface ClientDetail extends ClientRow {
  /**
   * Enrollment periods eligible for study access today, grouped by their
   * snapshotted tier. Read-only operational information, not a cap.
   */
  currentEnrollmentCountByTier: EnrollmentCountByTier
  hrUsers: HrUserRow[]
  /** Strict read-only projection; only the stored JSON boolean true consents. */
  transcriptImprovementConsent: boolean
}

export interface ClientListResponse {
  clients: ClientRow[]
  /** Matches of the current filters, independent of limit/offset. */
  total: number
}

/** The server's closed sort vocabulary — anything else is a 422. */
export type ClientSortField = 'name' | 'createdAt'
export type ClientSortDirection = 'asc' | 'desc'

export interface ListClientsParams {
  /** Server-side ilike over name + slug. Whitespace-only means unset. */
  search?: string
  /**
   * Fetch specific clients by id, whatever the search and sort. This is
   * how the picker keeps a SELECTED client's label rendered when that
   * client is absent from the current result page — including on a deep
   * link, where the browser holds an id and nothing else.
   */
  clientIds?: string[]
  /**
   * Restrict to clients a wave can land in (a live, unended cohort).
   * Three-state like `isInternal`: `undefined` does not filter.
   */
  hasJoinableCohort?: boolean
  /** Sorting runs in SQL, with a client-id tie-breaker for stable paging. */
  sortField?: ClientSortField
  sortDirection?: ClientSortDirection
  /**
   * The analytics carve-out, three-state at the boundary: `undefined` means
   * don't filter, which is NOT the same as `false` — omitting it returns both.
   *
   * Worth more here than on the student roster now that internal is sticky
   * downward: an internal CLIENT forces every cohort and student inside it,
   * so this answers a whole tree of classifications rather than one row.
   */
  clientEmployeeIdEnabled?: boolean
  clientDepartmentEnabled?: boolean
  isInternal?: boolean
  /** Page size, server max 200. */
  limit?: number
  offset?: number
}

export interface CreateClientBody {
  name: string
  slug: string
  contractTrophyTarget: number
  clientEmployeeIdEnabled?: boolean
  clientDepartmentEnabled?: boolean
  isInternal: boolean
}

/** Partial edit — omitted fields are left unchanged server-side. */
export interface UpdateClientBody {
  name?: string
  slug?: string
  contractTrophyTarget?: number
  clientEmployeeIdEnabled?: boolean
  clientDepartmentEnabled?: boolean
  isInternal?: boolean
}
